import {
    CredentialsAndIdentityId,
    CredentialsAndIdentityIdProvider,
    GetCredentialsOptions,
} from 'aws-amplify/auth'

import awsconfig from '../../aws-exports'

import { CognitoIdentity } from '@aws-sdk/client-cognito-identity'
import { logger } from 'app/logging/Logger'

// You can make use of the sdk to get identityId and credentials
const cognitoIdentity = new CognitoIdentity({
    region: awsconfig.aws_cognito_region,
})

// Note: The custom provider class must implement CredentialsAndIdentityIdProvider
export class AwsCredentialsProvider
    implements CredentialsAndIdentityIdProvider
{
    private cachedCredentialsAndIdentityId?: CredentialsAndIdentityId

    // Example class member that holds the login information
    federatedLogin?: {
        domain: string
        token: string
    }

    // Custom method to load the federated login information
    loadFederatedLogin(login?: typeof this.federatedLogin) {
        // You may also persist this by caching if needed
        this.federatedLogin = login
    }

    async getCredentialsAndIdentityId(
        getCredentialsOptions: GetCredentialsOptions
    ): Promise<CredentialsAndIdentityId | undefined> {
        if (!this.federatedLogin) {
            return Promise.resolve(undefined)
        }

        if (
            this.cachedCredentialsAndIdentityId &&
            !getCredentialsOptions.forceRefresh
        ) {
            // Convert expiration to Date if it's a string
            const expirationDate =
                typeof this.cachedCredentialsAndIdentityId.credentials
                    .expiration === 'string'
                    ? new Date(
                          this.cachedCredentialsAndIdentityId.credentials.expiration
                      )
                    : this.cachedCredentialsAndIdentityId.credentials.expiration

            // A cached entry without an expiry used to throw a TypeError on
            // `.getTime()`; keep throwing, with a clear message.
            if (!expirationDate) {
                throw new Error('Cached AWS credentials have no expiration')
            }

            const isCacheValid = expirationDate.getTime() > new Date().getTime()

            if (isCacheValid) {
                return Promise.resolve(this.cachedCredentialsAndIdentityId)
            }
        }

        try {
            const getIdResult = await cognitoIdentity.getId({
                // Get the identityPoolId from config
                IdentityPoolId: awsconfig.aws_cognito_identity_pool_id,
                Logins: {
                    [this.federatedLogin.domain]: this.federatedLogin.token,
                },
            })

            const cognitoCredentialsResult =
                await cognitoIdentity.getCredentialsForIdentity({
                    IdentityId: getIdResult.IdentityId,
                    Logins: {
                        [this.federatedLogin.domain]: this.federatedLogin.token,
                    },
                })

            const credentials = cognitoCredentialsResult.Credentials
            if (!credentials?.AccessKeyId || !credentials.SecretKey) {
                // Previously cached and returned keys of `undefined`, which
                // only failed later when a request was signed. Now handled
                // like any other credentials error below.
                throw new Error('Cognito returned no AWS access keys')
            }

            this.cachedCredentialsAndIdentityId = {
                credentials: {
                    accessKeyId: credentials.AccessKeyId,
                    secretAccessKey: credentials.SecretKey,
                    sessionToken: credentials.SessionToken,
                    expiration: credentials.Expiration,
                },
                identityId: getIdResult.IdentityId,
            }

            console.error(
                '[DIAG] AwsCredentialsProvider: got credentials, identityId:',
                getIdResult.IdentityId,
                'hasAccessKeyId:',
                !!cognitoCredentialsResult.Credentials?.AccessKeyId
            )

            return this.cachedCredentialsAndIdentityId
        } catch (e) {
            logger.error('Error getting AWS credentials', e as Error, {
                action: 'get_aws_credentials_error',
            })
        }
    }
    // Implement this to clear any cached credentials and identityId. This can be called when signing out of the federation service.
    clearCredentialsAndIdentityId(): void {}
}
